import { Injectable } from '@angular/core';

const processedImageCache = new Map<string, Promise<string>>();

@Injectable({
  providedIn: 'root',
})
export class ImageProcessorService {

  /**
   * Processes a Base64 string to convert transparent areas to white.
   * Uses caching to prevent reprocessing.
   * @param base64String The Base64 string of the image (e.g., 'data:image/png;base64,...').
   * @returns A Promise that resolves with the new Base64 string of the image with a white background.
   */
  public setTransparentToWhiteFromBase64(base64String: string): Promise<string> {
    if (processedImageCache.has(base64String)) {
      return processedImageCache.get(base64String)!;
    }

    const processingPromise: Promise<string> = new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        return reject(new Error('Could not get 2D canvas context.'));
      }
      img.src = base64String;

      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        for (let i = 0; i < data.length; i += 4) {
          const alpha = data[i + 3];

          if (alpha === 0) {
            data[i] = 255;
            data[i + 1] = 255;
            data[i + 2] = 255;
            data[i + 3] = 255;
          }
        }

        ctx.putImageData(imageData, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };

      img.onerror = (e) => reject(new Error('Error loading Base64 image.'));
    });
    processedImageCache.set(base64String, processingPromise);
    return processingPromise;
  }
}
